import { Controller } from "@hotwired/stimulus"

// Blocks submit on empty required fields and points the browser's own validation
// tip at the offending input.
//
// Meant for forms whose fields are shown and hidden by other controllers (a
// provider picker, a section toggle, a masked secret). HTML5 `required` cannot
// carry those forms: the browser refuses to submit while a required field is
// hidden but has nowhere to anchor its tip, so Save appears to do nothing. That
// is why such forms are marked `novalidate` — an explicit `reportValidity()` on
// an element still shows the tip, since `novalidate` only suppresses the
// browser's automatic pass over the whole form.
//
// A field counts as required when its label is showing the required asterisk
// (`label.required`), which is the same state the surrounding controllers
// already maintain as they show and hide groups. Nothing to keep in sync here,
// and the rule matches what the user sees.
export default class extends Controller {
  static values = { message: { type: String, default: "Please fill in the required field." } }

  // Checkboxes and radios are absent rather than empty, so `value` says nothing
  // about whether they were filled in.
  static SKIPPED_TYPES = ["checkbox", "radio", "hidden"]

  validate(event) {
    const field = this.missingFields()[0]
    if (!field) return

    event.preventDefault()
    // Keeps @rails/ujs's document-level submit handler from disabling the submit
    // button on a cancelled submission.
    event.stopPropagation()

    field.setCustomValidity(this.messageValue)
    field.reportValidity()
    field.scrollIntoView({ block: "center" })
  }

  // A custom validity sticks until it is cleared, so a field left invalid would
  // keep failing `reportValidity()` after the user fixes it.
  clear(event) {
    if (typeof event.target.setCustomValidity === "function") {
      event.target.setCustomValidity("")
    }
  }

  missingFields() {
    return Array.from(this.element.querySelectorAll("label.required"))
      .map((label) => this.fieldFor(label))
      .filter((field) => this.isEnforceable(field) && field.value.trim() === "")
  }

  // Only explicit labels resolve, which is what these forms use. A secret field
  // renders a masked placeholder input alongside the real one, and the `for`
  // always points at the real one.
  fieldFor(label) {
    const id = label.getAttribute("for")
    return id ? document.getElementById(id) : null
  }

  // Visibility is read off the page rather than tracked, so no state has to be
  // kept in step with the controllers doing the showing and hiding. A disabled
  // field is never submitted, so there is nothing to enforce on it.
  isEnforceable(field) {
    if (!field || field.disabled) return false
    if (this.constructor.SKIPPED_TYPES.includes(field.type)) return false

    return !this.isHidden(field)
  }

  isHidden(field) {
    for (let node = field; node; node = node.parentElement) {
      if (getComputedStyle(node).display === "none") return true
      if (node === this.element) break
    }

    return false
  }
}
